import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { BrandForm } from "./brand-form";

import { FormError, FormModal } from "@/components/kit";
import { Brand } from "@/types";
import { BRAND_FORM_SCHEMA, BrandFormData } from "@/schemas/brand-form.schema";
import { useEditBrand } from "@/hooks/api/brand/useEditBrand";

interface EditBrandModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  brand: Brand | null;
}

export function EditBrandModal({
  isOpen,
  onClose,
  onSuccess,
  brand,
}: EditBrandModalProps) {
  const form = useForm({
    resolver: zodResolver(BRAND_FORM_SCHEMA),
    mode: "all",
    defaultValues: {
      id: "",
      image: "",
      name: "",
      nameAr: "",
      isActive: true,
      isLocal: false,
    },
  });

  const {
    mutate: editBrand,
    isPending: isEditingBrand,
    error,
    isError,
    reset: resetMutation,
  } = useEditBrand(brand?.id!);

  const errorMessage = isError
    ? isAxiosError(error)
      ? error.response?.data?.message
      : "An error occured while updating the brand, please try again!"
    : null;

  const handleSubmit = (data: BrandFormData) => {
    editBrand(data, { onSuccess });
  };

  useEffect(() => {
    if (brand)
      form.reset({
        id: brand.id,
        image: brand.image,
        name: brand.name,
        nameAr: brand.nameAr || "",
        isActive: brand.isActive,
        isLocal: brand.isLocal,
      });
  }, [brand]);

  return (
    <FormModal
      isOpen={isOpen}
      isSubmitting={isEditingBrand}
      submitLabel="Save"
      title="Edit brand"
      onClose={() => {
        resetMutation();
        onClose();
      }}
      onSubmit={form.handleSubmit(handleSubmit)}
    >
      <FormProvider {...form}>
        <FormError message={errorMessage} />
        <BrandForm disabledFields={["id"]} />
      </FormProvider>
    </FormModal>
  );
}
